import Button from 'core/components/buttons/Button'
import useCurrentTeam from 'core/hooks/useCurrentTeam'
import { useUserProfile } from 'core/hooks/useDataHook'
import useNotifications from 'core/hooks/useNotifications'
import usePathSection from 'core/hooks/usePathSection'
import { useEffect } from 'react'
import { useMutation } from 'react-query'
import { InstitutionUser } from 'types/entity'
import APIService from 'api/APIService'
import { PlaidHandlers } from 'utils/TrackPlaidEvents'
import {
  PlaidLinkError,
  PlaidLinkOnEventMetadata,
  PlaidLinkOnExitMetadata,
  PlaidLinkOnSuccessMetadata
} from 'react-plaid-link'
import useSubscription from 'core/hooks/useSubscription'

const plaidOptions = {
  env: process.env.REACT_APP_PLAID_ENV,
  apiVersion: 'v2',
  clientName: 'Everlance',
  key: process.env.REACT_APP_PLAID_PUBLIC_KEY,
  product: ['transactions'],
  onLoad: () => null,
  onExit: function (err: unknown) {
    // The user exited the Link flow.
    if (err != null) {
      // The user encountered a Plaid API error prior
      // to exiting.
      console.log('Plaid error:', err)
    }
    // metadata contains information about the institution
    // that the user selected and the most recent API request
    // IDs. Storing this information is helpful for support.
  }
}

const ExpiredBankNotification = () => {
  const { paywalledFeatures } = useSubscription()
  const { data } = useUserProfile()
  const { data: currentTeam } = useCurrentTeam()
  const { info } = useNotifications()
  const { isTeamSpace } = usePathSection()

  const { institution_users } = data ?? {}
  const { bank_integration_enabled } = currentTeam ?? {}
  const expiredBank = institution_users?.find((iu) => iu?.login_expiration)
  const { institution_name } = expiredBank ?? {}

  const bank_id = expiredBank?.token_id

  const { mutate: createLinkToken } = useMutation(
    ['createLinkToken'],
    APIService.misc.createLinkToken,
    {
      onSuccess: (data) => {
        const { link_token } = data
        const linkHandler = window.Plaid.create({
          ...plaidOptions,
          token: link_token,
          onSuccess: function (
            public_token: string,
            metadata: PlaidLinkOnSuccessMetadata
          ) {
            PlaidHandlers.UpdatePlaid.onSuccess({
              token_id: bank_id as string,
              metadata,
              link_token
            })
          },
          onEvent: function (
            eventName: string,
            metadata: PlaidLinkOnEventMetadata
          ) {
            PlaidHandlers.UpdatePlaid.onEvent({
              eventName,
              metadata,
              link_token
            })
          },
          onExit: function (
            err: PlaidLinkError | null,
            metadata: PlaidLinkOnExitMetadata
          ) {
            PlaidHandlers.UpdatePlaid.onExit({
              metadata,
              link_token,
              error: err
            })
          }
        })

        linkHandler.open()
      }
    }
  )

  const { mutate: getInstitutionToken } = useMutation(
    ['updateLogin'],
    (bankId: InstitutionUser['token_id']) =>
      APIService.misc.getInstitutionToken(bankId),
    {
      onSuccess: () => {
        createLinkToken({ token_id: bank_id as string })
      }
    }
  )

  useEffect(() => {
    if (
      expiredBank &&
      bank_integration_enabled !== false &&
      !isTeamSpace &&
      paywalledFeatures.expenses_card_sync
    ) {
      //show a call to action for user to update their plaid login
      setTimeout(() => {
        info({
          id: `expired-bank-${bank_id}}`,
          title: 'Please renew bank login',
          subtitle: `To continue pulling your transactions from your ${institution_name} account, we need you to renew your login`,
          footer: (dismiss) => (
            <div className="flex justify-end">
              <Button
                variant="primary"
                onClick={() => {
                  getInstitutionToken(bank_id as string)
                  dismiss()
                }}
              >
                Reconnect your bank account
              </Button>
            </div>
          ),
          duration: Infinity
        })
      }, 1000)
    }
  }, [
    bank_id,
    bank_integration_enabled,
    expiredBank,
    info,
    institution_name,
    isTeamSpace,
    getInstitutionToken,
    paywalledFeatures.expenses_card_sync
  ])

  return null
}

export default ExpiredBankNotification
